import { ApiPaths } from 'api/constants';
import {
  UpdateUserActiveTenantResult,
  User,
  UserProfile,
} from 'api/endpoints/users';
import { tenantCache } from 'api/tenantCache';
import BaseRepo from 'container/BaseRepo';
import { buildBadRequestApiError } from 'container/helpers/errors';
import { ApiResponseAsync, isApiError } from 'container/models';
import { Patch } from 'container/patch';

import { AccountLinkingInfo, IUserRepo, PasswordResetInfo } from './IUserRepo';

export class UserRepo extends BaseRepo implements IUserRepo {
  setNewPasswordFromReset = (
    token: string,
    newPassword: string
  ): ApiResponseAsync<void> =>
    this.helpers.handlePostAction(ApiPaths.auth.restorePassword(), {
      ott: token,
      password: newPassword,
    });

  validatePasswordResetToken = (
    token: string
  ): ApiResponseAsync<PasswordResetInfo> => {
    const params = new URLSearchParams();
    params.set('ott', token);

    return this.helpers.handleGet(
      ApiPaths.auth.validateForgotPassword() + '?' + params.toString()
    );
  };

  updateUserActiveTenant = async (
    tenantId: string
  ): ApiResponseAsync<UserProfile> => {
    const link = ApiPaths.users.profile._();

    const response = await this.helpers.handlePut<
      {
        currentTenantId: string;
      },
      UpdateUserActiveTenantResult
    >(link, {
      currentTenantId: tenantId,
    });

    if (isApiError(response)) return response;

    tenantCache.handleUpdate(response);

    return response;
  };

  requestPasswordReset = (username: string): ApiResponseAsync<void> =>
    this.helpers.handlePostAction(ApiPaths.auth.forgotPassword(), { username });

  getCurrentUserInfo = async (): ApiResponseAsync<User> => {
    const user = await this.helpers.handleGet<User>(ApiPaths.users.info._());

    if (isApiError(user)) return user;

    user.acceptedPrivacyPolicyAt =
      user.acceptedPrivacyPolicyAt && new Date(user.acceptedPrivacyPolicyAt);

    user.acceptedTermsAt =
      user.acceptedTermsAt && new Date(user.acceptedTermsAt);

    tenantCache.initFromUser(user);

    return user;
  };

  updateUserTermsAcceptance = (
    user: User,
    patch: Patch
  ): ApiResponseAsync<void> => {
    const link = user._links?.['updateUserPolicies'];
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "update user policies" is not available')
        )
      );
    }

    return this.helpers.executePatch(link, patch);
  };

  getUserAccountLinkingState = (
    user: User
  ): ApiResponseAsync<AccountLinkingInfo> => {
    const link = user._links?.['userCanBeLinkedToIdentityProvider'];
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "userCanBeLinkedToIdentityProvider" is not available')
        )
      );

    return this.helpers.handleGet(link.href);
  };

  updateCurrentUserActiveTenant = (
    tenantId: string
  ): ApiResponseAsync<UserProfile> => this.updateUserActiveTenant(tenantId);
}
